import React, { useEffect, useRef, useState } from 'react';
import { FaCircleCheck, FaCircleXmark } from 'react-icons/fa6';
import { postJSON } from '../lib/api';
import {
  getCompletion,
  markSelfCheckComplete,
} from '../lib/selfCheckProgress';

// Inline Check-Your-Understanding card. Drops in wherever the lesson
// markdown has a `{{check:slug}}` marker — see LessonBody.js for the
// splitter.
//
// UX (auto-submit, no buttons):
//   - Single-correct multiple choice.
//   - Selecting any option auto-fires POST /api/quizzes/attempts.
//   - WRONG: the picked option turns red + shows X + becomes disabled (it
//     can't be re-selected). Other options remain active. An inline
//     "Not quite — try another answer." message appears. Selecting any
//     other option fires the next attempt. No buttons anywhere.
//   - RIGHT: every option locks. The right pick turns green with a check.
//     The explanation appears below as positive reinforcement. The
//     completion is recorded in localStorage (selfCheckProgress) so a page
//     reload restores the answered state, AND so the engagement gate on
//     the course page can count this question toward "X of 48 done."
//   - On mount: if the question is already in the completed set, jump
//     straight to the locked/correct state without contacting the server.
//
// Anti-gaming notes:
//   - Wrong picks become permanently disabled per session, so the worst-case
//     "click everything fast" path still forces engagement with at least one
//     question explanation before the card unlocks.
//   - Completion = the SERVER said the answer was correct. The client never
//     decides on its own. This is true today (in-memory eval, no quiz_attempts
//     row) and will remain true once auth lands and rows persist per-user.
export default function QuizCheck({ question, courseSlug }) {
  // disabledIndices: the set of options the learner has tried and gotten
  // wrong. They stay greyed out + marked X for the rest of the session.
  const [disabledIndices, setDisabledIndices] = useState([]);
  // selected: the most-recently-clicked index. After a wrong pick, this
  // points at the wrong option (so it shows the X) until they pick again.
  // After a correct pick, this points at the correct option and the card
  // is locked.
  const [selected, setSelected] = useState(null);
  // wrongMessage: visible only after a wrong pick, cleared when they pick
  // again so the message doesn't linger past its relevance.
  const [wrongMessage, setWrongMessage] = useState(false);
  // result.correct + correct_index + explanation. Only ever set on the
  // CORRECT submission — wrong submissions update disabledIndices/selected
  // but don't populate this, so we never show the right answer prematurely.
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // Ref-guard against double-clicks while submitting is in flight (state
  // updates aren't synchronous, so two fast clicks can both pass the check).
  const submittingRef = useRef(false);
  const questionLabelId = `quiz-q-${question.id}-label`;

  // On mount + whenever the question id changes, restore the locked-correct
  // state from localStorage if this question was previously completed.
  // Skip sample questions — they don't count and the user may want to retry.
  //
  // CRITICAL guard: only restore when the cached entry includes a valid
  // `correct_index`. Placeholder entries (review-mode bypass, legacy format)
  // must NOT make the UI show "Correct!" with nothing highlighted.
  useEffect(() => {
    if (question.is_sample || !courseSlug) return;
    const cached = getCompletion(courseSlug, question.id);
    if (!cached || !Number.isFinite(cached.correct_index)) return;
    setResult({
      is_correct: true,
      correct_index: cached.correct_index,
      explanation: cached.explanation || null,
    });
    setSelected(cached.correct_index);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question.id, courseSlug]);

  // Listen for bulk localStorage restores (fired by restoreCompletions after
  // the server fetch resolves on login). The mount effect above only runs
  // once — if the server data arrives after mount, this effect picks up the
  // change and applies the locked-correct state without a remount.
  useEffect(() => {
    if (question.is_sample || !courseSlug) return undefined;
    const onProgress = () => {
      const cached = getCompletion(courseSlug, question.id);
      if (!cached || !Number.isFinite(cached.correct_index)) return;
      setResult({
        is_correct: true,
        correct_index: cached.correct_index,
        explanation: cached.explanation || null,
      });
      setSelected(cached.correct_index);
    };
    window.addEventListener('lo:self_check_progress', onProgress);
    return () => window.removeEventListener('lo:self_check_progress', onProgress);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question.id, courseSlug]);

  const locked = !!(result && result.is_correct);

  const submitAttempt = async (idx) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setError(null);
    try {
      // postJSON attaches X-Review-Token; bypassing it 403s in cookie-blocked
      // iframe contexts (the very case we test in).
      const payload = await postJSON('/api/quizzes/attempts', {
        question_id: question.id,
        selected_index: idx,
      });
      if (payload.is_correct) {
        setResult(payload);
        setWrongMessage(false);
        if (!question.is_sample && courseSlug) {
          // Record the completion (and cache correct_index + explanation
          // so a future reload restores the locked-correct state without
          // another server hit).
          markSelfCheckComplete(courseSlug, question.id, {
            correctIndex: payload.correct_index,
            explanation: payload.explanation || null,
          });
        }
      } else {
        // Wrong: disable that option, surface the inline prompt, leave
        // `result` untouched so the right answer stays hidden.
        setDisabledIndices((prev) => (prev.includes(idx) ? prev : [...prev, idx]));
        setWrongMessage(true);
      }
    } catch (err) {
      setError(err.message || 'Something went wrong submitting your answer.');
      // On a network error we DON'T mark the option as disabled — the
      // learner should be able to retry the same pick once the network
      // recovers, since the server never actually evaluated it.
    } finally {
      setSubmitting(false);
      submittingRef.current = false;
    }
  };

  const handleSelect = (idx) => {
    if (locked || submitting) return;
    if (disabledIndices.includes(idx)) return;
    setSelected(idx);
    // Selecting a different option after a wrong attempt clears the inline
    // prompt — it'll re-appear if this attempt is also wrong, or stay
    // hidden if it's right (in which case the success block takes over).
    setWrongMessage(false);
    submitAttempt(idx);
  };

  // Sample questions are previews shown to give learners a feel for the quiz
  // format. They evaluate the same way (right/wrong feedback, explanation on
  // correct), but they don't count toward progress and the card labels
  // itself accordingly so the learner knows it's just a demo.
  const isSample = !!question.is_sample;

  return (
    <aside id={`check-${question.position_marker || question.id}`} className="my-10 not-prose rounded-2xl border border-brand-deep/20 bg-cream/70 shadow-soft overflow-hidden scroll-mt-24">
      <header className="px-6 sm:px-7 py-4 bg-brand-deep/8 border-b border-brand-deep/15 flex items-center gap-3">
        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-brand-deep text-bone">
          <FaCircleCheck className="text-sm" />
        </span>
        <div className="min-w-0">
          <div className="text-[10px] tracking-[0.18em] uppercase text-brand-aubergine">
            Check Your Understanding
          </div>
          <div className="text-xs text-slate2 mt-0.5">
            Skill: <span className="text-ink">{question.skill_label}</span>
            {isSample && (
              <span className="block italic mt-0.5">
                Just a preview — this one isn&rsquo;t counted toward your progress.
              </span>
            )}
          </div>
        </div>
      </header>

      <div className="px-6 sm:px-7 py-6">
        <p id={questionLabelId} className="font-display text-lg text-ink leading-snug">
          {question.question}
        </p>

        {/* `!list-none !pl-0` — important modifier needed because the parent
            article's `.prose-lesson ul` rule in src/index.css applies
            `list-disc pl-5` with higher selector specificity than a plain
            `list-none` class. Without `!`, the radio choices show stray
            decorative bullets to the left of the radio inputs. */}
        <ul role="radiogroup" aria-labelledby={questionLabelId} className="mt-5 space-y-2.5 !list-none !pl-0">
          {question.options.map((opt, idx) => {
            const isSelected = selected === idx;
            const isDisabled = locked || disabledIndices.includes(idx) || submitting;
            const showRightHere = locked && result && result.correct_index === idx;
            const showWrongHere = disabledIndices.includes(idx);

            let cls =
              'flex items-start gap-3 px-4 py-3 rounded-xl border transition-colors';
            if (isDisabled && !showRightHere && !showWrongHere) {
              cls += ' cursor-default opacity-70';
            } else if (locked) {
              cls += ' cursor-default';
            } else {
              cls += ' cursor-pointer';
            }
            if (showRightHere) {
              cls += ' border-brand-moss bg-brand-moss/10 text-ink';
            } else if (showWrongHere) {
              cls += ' border-rose-400 bg-rose-50 text-ink';
            } else if (isSelected && !showWrongHere) {
              cls += ' border-brand-deep bg-brand-deep/8 text-ink';
            } else {
              cls += ' border-ink/15 bg-white text-ink hover:border-ink/30';
            }
            return (
              <li key={idx}>
                <label className={cls}>
                  <input
                    type="radio"
                    name={`q-${question.id}`}
                    className="mt-1 accent-brand-deep"
                    checked={isSelected}
                    disabled={isDisabled}
                    onChange={() => handleSelect(idx)}
                  />
                  <span className="text-sm leading-relaxed flex-1">{opt}</span>
                  {showRightHere && (
                    <FaCircleCheck className="text-brand-moss mt-1 flex-shrink-0" />
                  )}
                  {showWrongHere && (
                    <FaCircleXmark className="text-rose-500 mt-1 flex-shrink-0" />
                  )}
                </label>
              </li>
            );
          })}
        </ul>

        {error && (
          <div role="alert" className="mt-4 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        {/* Wrong-attempt prompt. Stays visible only until the next pick. */}
        {wrongMessage && !locked && (
          <div
            role="status"
            aria-live="polite"
            className="mt-5 rounded-xl px-4 py-3 text-sm leading-relaxed bg-amber-50 border border-amber-300 text-ink"
          >
            <div className="font-display text-base">
              Not quite — try another answer.
            </div>
          </div>
        )}

        {/* Correct-state success block. Only mounts once locked. */}
        {locked && (
          <div
            role="status"
            aria-live="polite"
            className="mt-5 rounded-xl px-4 py-3 text-sm leading-relaxed bg-brand-moss/10 border border-brand-moss/30 text-ink"
          >
            <div className="font-display text-base mb-1">Correct!</div>
            {result && result.explanation && <div>{result.explanation}</div>}
          </div>
        )}
      </div>
    </aside>
  );
}
